import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, FormControl,
  FormControlLabel, Grid, InputAdornment, InputLabel, MenuItem, Select,
  Switch, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  CardGiftcard, Insights, Redeem, Add, Block, Refresh, PauseCircleFilled,
  ArrowForward, InfoOutlined, ExpandMore, ExpandLess, CheckCircle,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { colors } from '../theme/theme';
import { useAuth } from '../contexts/AuthContext';
import StudioSelector, { StudioOption } from '../components/StudioSelector';
import { STUDIO_SCOPED_ROLES, isOwnerRole } from '../utils/roles';
import { useMyLocations } from '../hooks/useMyLocations';
import BranchSelect from '../components/BranchSelect';

/**
 * Referrals — member-get-member, studio by studio.
 *
 * Three things about this page are deliberate:
 *
 *  1. **The studio is never sent by a studio user.** The backend resolves it
 *     from the caller's token. `studioId` is passed only for a super admin
 *     driving the picker, and the backend ignores it for everyone else.
 *
 *  2. **A percentage reward always has a ceiling.** `maxDiscount` is required
 *     for `percent`, and the form refuses to submit without it — every reward
 *     here is funded by the studio, so an uncapped percentage is an unbounded
 *     liability against their own money.
 *
 *  3. **Live-vs-paused is the loudest thing on the page.** A saved programme is
 *     invisible to members until it is switched on, and both this page and the
 *     app render a paused programme as "no programme". That silence cost a real
 *     debugging session, so the state now leads the layout and the only primary
 *     button on a paused page is the one that starts it.
 */

// ── types mirroring the backend responses ───────────────────────────────────

type RewardKind = 'percent' | 'amount' | 'free_item' | 'pack_credit';

interface RewardSpec {
  kind: RewardKind;
  value: number;
  maxDiscount?: number | null;
  modules?: string[];
  classTypes?: string[];
}

interface Program {
  _id?: string;
  name: string;
  scope: 'studio' | 'branches';
  codePrefix: string;
  isActive: boolean;
  refereeReward: RewardSpec;
  referrerReward?: RewardSpec | null;
  qualifyingEvent: 'referee_attended' | 'referee_signup';
  referrerCapPerMonth?: number | null;
  referrerCapLifetime?: number | null;
  rewardExpiryDays: number;
  codeExpiryDays?: number | null;
  refereeRewardOnTrial: boolean;
  terms?: string;
}

interface ReportShape {
  funnel: {
    invited: number; joined: number; attended: number; rewarded: number;
    rejected: number; expired: number; joinRate: number; attendRate: number;
  };
  cost: number;
  revenueFromReferred: number;
  rows: any[];
}

interface LedgerShape {
  rows: any[];
  total: number;
  page: number;
  pages: number;
  summary: { issued: number; redeemed: number; cost: number; expiringSoon: number };
}

/**
 * Only the two kinds that can actually be SPENT.
 *
 * `pack_credit` and `free_item` exist in the schema and can be minted, but
 * benefits.bestFor excludes pack_credit outright and nothing consumes
 * free_item — a member awarded either would carry it in their wallet forever
 * and never be able to use it. Offering them here would issue dead rewards, so
 * they stay out of the UI until there is a redemption path.
 */
const REWARD_KINDS: { value: RewardKind; label: string }[] = [
  { value: 'percent', label: 'Percentage off' },
  { value: 'amount', label: 'Flat amount off' },
];

const MODULES: { key: string; label: string }[] = [
  { key: 'session', label: 'Classes' },
  { key: 'pack', label: 'Packs' },
  { key: 'event', label: 'Events' },
  { key: 'merchandise', label: 'Shop' },
];

const EMPTY_PROGRAM: Program = {
  name: 'Referral programme',
  scope: 'branches',
  codePrefix: '',
  isActive: false,
  refereeReward: { kind: 'percent', value: 10, maxDiscount: 200, modules: ['session', 'pack'] },
  referrerReward: { kind: 'percent', value: 10, maxDiscount: 200, modules: ['session', 'pack'] },
  qualifyingEvent: 'referee_attended',
  referrerCapPerMonth: 5,
  referrerCapLifetime: null,
  rewardExpiryDays: 30,
  codeExpiryDays: null,
  refereeRewardOnTrial: false,
  terms: '',
};

/** First-run choices. Each states its worst-case cost, because that is the decision. */
const PRESETS: {
  id: string; title: string; sub: string; cost: string;
  referee: RewardSpec; referrer: RewardSpec | null;
}[] = [
  {
    id: 'balanced',
    title: '10% off for both, up to ₹200',
    sub: 'Most studios start here',
    cost: '≤ ₹400 per invite',
    referee: { kind: 'percent', value: 10, maxDiscount: 200, modules: ['session', 'pack'] },
    referrer: { kind: 'percent', value: 10, maxDiscount: 200, modules: ['session', 'pack'] },
  },
  {
    id: 'friend-only',
    title: '₹300 off for the friend only',
    sub: 'Cheaper, but members have less reason to share',
    cost: '≤ ₹300 per invite',
    referee: { kind: 'amount', value: 300, modules: ['session', 'pack'] },
    referrer: null,
  },
  {
    id: 'generous',
    title: '15% off for both, up to ₹500',
    sub: 'Stronger pull when you are filling a new studio',
    cost: '≤ ₹1,000 per invite',
    referee: { kind: 'percent', value: 15, maxDiscount: 500, modules: ['session', 'pack'] },
    referrer: { kind: 'percent', value: 15, maxDiscount: 500, modules: ['session', 'pack'] },
  },
];

const inr = (n: number) =>
  `₹${(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const describeReward = (r?: RewardSpec | null): string => {
  if (!r) return 'Nothing';
  switch (r.kind) {
    case 'percent':
      return `${r.value}% off${r.maxDiscount ? ` (max ${inr(r.maxDiscount)})` : ''}`;
    case 'amount':
      return `${inr(r.value)} off`;
    case 'pack_credit':
      return `${r.value} free class${r.value === 1 ? '' : 'es'}`;
    case 'free_item':
      return 'A free item';
    default:
      return String(r.kind);
  }
};

/** The most a single successful invite can cost the studio. */
const worstCase = (r?: RewardSpec | null): number => {
  if (!r) return 0;
  if (r.kind === 'percent') return Number(r.maxDiscount) || 0;
  if (r.kind === 'amount') return Number(r.value) || 0;
  return 0;
};

const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  invited: 'default', signed_up: 'info', qualified: 'warning',
  rewarded: 'success', rejected: 'error', expired: 'default',
  active: 'info', redeemed: 'success', revoked: 'error',
};

const STAGE_LABEL: Record<string, string> = {
  invited: 'Shared, no takers',
  signed_up: 'Joined, not in yet',
  qualified: 'Came in',
  rewarded: 'Rewarded',
  rejected: 'Not eligible',
  expired: 'Expired',
};

// ── small shared pieces ─────────────────────────────────────────────────────

const SectionCard: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
  <Card sx={{ ...sx }}><CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>{children}</CardContent></Card>
);

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      fontSize: '0.75rem', fontWeight: 600, color: colors.greyText2,
      letterSpacing: '0.04em', textTransform: 'uppercase',
    }}
  >
    {children}
  </Typography>
);

/**
 * The programme's state, as the first thing on the page.
 *
 * Paused is deliberately loud and carries the page's only contained button:
 * the previous design saved a programme and then said nothing, so a studio
 * could sit on an invisible programme indefinitely believing it was running.
 */
const StatusBanner: React.FC<{
  isActive: boolean;
  canActivate: boolean;
  busy: boolean;
  onToggle: (next: boolean) => void;
}> = ({ isActive, canActivate, busy, onToggle }) => {
  if (isActive) {
    return (
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.75, p: '13px 18px', mb: 2.5,
          borderRadius: '14px', bgcolor: '#f1f8f2', border: '1px solid #b9dcbd',
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors.success, flexShrink: 0 }} />
        <Typography sx={{ flexGrow: 1, fontSize: '0.875rem', color: colors.textPrimary }}>
          <strong>Live</strong>
          <Box component="span" sx={{ color: colors.textSecondary }}>
            {' — members can generate codes and claim rewards.'}
          </Box>
        </Typography>
        <Tooltip title={canActivate ? '' : 'Only the studio owner can pause the programme.'}>
          <span>
            <Button
              size="small" color="inherit" disabled={!canActivate || busy}
              onClick={() => onToggle(false)}
              sx={{ color: colors.textSecondary, fontWeight: 600 }}
            >
              Pause
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, p: '16px 18px', mb: 2.5,
        borderRadius: '14px', bgcolor: '#fff8e9', border: '1px solid #ffd88a',
      }}
    >
      <Box
        sx={{
          flexShrink: 0, width: 38, height: 38, borderRadius: '50%', bgcolor: colors.warning,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <PauseCircleFilled sx={{ color: '#fff', fontSize: 22 }} />
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: colors.textPrimary }}>
          Paused — no member can see this
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: colors.textSecondary, lineHeight: 1.5 }}>
          Your programme is saved but not running. Nobody can generate a code or claim a
          reward until you start it.
        </Typography>
      </Box>
      <Tooltip title={canActivate ? '' : 'Only the studio owner can start the programme.'}>
        <span>
          <Button
            variant="contained" disabled={!canActivate || busy}
            onClick={() => onToggle(true)} sx={{ flexShrink: 0 }}
          >
            Start programme
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

/**
 * One reward, written as a sentence.
 *
 * The fields are unchanged (kind, value, maxDiscount, modules) — only the
 * arrangement is. A studio owner reading "the friend gets 10% off but never
 * more than ₹200" does not have to assemble that meaning from four separately
 * labelled inputs, which is what the previous grid asked of them.
 */
const RewardSentence: React.FC<{
  title: string;
  subject: string;
  value: RewardSpec | null | undefined;
  onChange: (r: RewardSpec | null) => void;
  allowNone?: boolean;
  footer?: React.ReactNode;
}> = ({ title, subject, value, onChange, allowNone, footer }) => {
  const r = value ?? null;
  const set = (patch: Partial<RewardSpec>) =>
    onChange({ ...(r ?? EMPTY_PROGRAM.refereeReward), ...patch });

  const toggleModule = (key: string) => {
    const cur = r?.modules ?? [];
    const next = cur.includes(key) ? cur.filter((m) => m !== key) : [...cur, key];
    set({ modules: next });
  };

  return (
    <SectionCard sx={{ height: '100%' }}>
      <Box display="flex" alignItems="center" gap={1.25} mb={2}>
        <Box
          sx={{
            width: 30, height: 30, borderRadius: '50%', bgcolor: '#fdeff5',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <CardGiftcard sx={{ fontSize: 17, color: colors.primaryDark }} />
        </Box>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, color: colors.textPrimary }}>
          {title}
        </Typography>
        {allowNone && (
          <FormControlLabel
            sx={{ ml: 'auto', mr: 0 }}
            control={
              <Switch
                size="small" checked={!!r}
                onChange={(e) => onChange(e.target.checked ? EMPTY_PROGRAM.refereeReward : null)}
              />
            }
            label=""
          />
        )}
      </Box>

      {!r ? (
        <Typography sx={{ fontSize: '0.875rem', color: colors.greyText2, py: 1 }}>
          {subject} gets nothing. Invites are still tracked, but there is less reason to share.
        </Typography>
      ) : (
        <>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
            <TextField
              size="small" type="number" value={r.value}
              onChange={(e) => set({ value: Number(e.target.value) })}
              sx={{ width: 84, '& input': { fontWeight: 700 } }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={r.kind}
                onChange={(e) => set({ kind: e.target.value as RewardKind })}
              >
                {REWARD_KINDS.map((k) => (
                  <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {r.kind === 'percent' && (
              <>
                <Typography sx={{ fontSize: '0.9375rem', color: colors.textSecondary }}>
                  but never more than
                </Typography>
                <TextField
                  size="small" type="number" required
                  value={r.maxDiscount ?? ''}
                  error={!r.maxDiscount}
                  onChange={(e) =>
                    set({ maxDiscount: e.target.value === '' ? null : Number(e.target.value) })
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  sx={{ width: 132, '& input': { fontWeight: 700 } }}
                />
              </>
            )}
          </Box>

          {r.kind === 'percent' && !r.maxDiscount && (
            <Typography sx={{ fontSize: '0.75rem', color: colors.error, mt: 1 }}>
              Required — without a ceiling this reward has no upper cost.
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Eyebrow>Can be spent on</Eyebrow>
          <Box display="flex" flexWrap="wrap" gap={0.75} mt={1}>
            {MODULES.map((m) => {
              const on = (r.modules ?? []).includes(m.key);
              return (
                <Chip
                  key={m.key} label={m.label} size="small" onClick={() => toggleModule(m.key)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: on ? '#fdeff5' : colors.backgroundTertiary,
                    color: on ? colors.secondaryDark : colors.greyText2,
                    border: on ? 'none' : `1px dashed ${colors.divider}`,
                    fontWeight: 500,
                  }}
                />
              );
            })}
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: colors.greyText2, mt: 1 }}>
            Greyed items are off — the reward will not apply there.
          </Typography>

          {footer}
        </>
      )}
    </SectionCard>
  );
};

// ── page ────────────────────────────────────────────────────────────────────

const ReferralsPage: React.FC = () => {
  const { admin } = useAuth();
  const role = admin?.role ?? '';
  const isStudioRole = STUDIO_SCOPED_ROLES.includes(role);
  const isSuperAdmin = role === 'super_admin' || role === 'admin';
  // A manager may configure the rewards but not commit the studio to paying
  // them. Platform admins pass too — they drive the studio picker.
  const canActivate = isOwnerRole(role) || isSuperAdmin;

  const referralsEnabled =
    (admin?.studio as any)?.studioPermissions?.enableReferrals === true;
  const blocked = isStudioRole && !referralsEnabled;

  const [selectedStudio, setSelectedStudio] = useState<StudioOption | null>(null);
  const studioParam = isSuperAdmin ? selectedStudio?._id : undefined;
  // A programme belongs to one location. Under "All locations" a studio role
  // picks which one — the switcher's __all__ is refused for writes, and the
  // reads would otherwise show the sign-in branch's programme as the brand's.
  const { isAllLocations } = useMyLocations();
  const [branch, setBranch] = useState('');
  const pickBranch = isStudioRole && isAllLocations;
  const pinStudioId = pickBranch ? branch || undefined : undefined;
  const needsStudio = (isSuperAdmin && !selectedStudio) || (pickBranch && !branch);

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [program, setProgram] = useState<Program>(EMPTY_PROGRAM);
  const [programExists, setProgramExists] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preset, setPreset] = useState<string>('balanced');
  // "Set it up myself" has no programme to load yet, so `programExists` stays
  // false and the first-run screen would keep rendering. This is what lets the
  // editor open on an unsaved draft.
  const [customSetup, setCustomSetup] = useState(false);

  const [report, setReport] = useState<ReportShape | null>(null);
  const [ledger, setLedger] = useState<LedgerShape | null>(null);
  const [ledgerStatus, setLedgerStatus] = useState('');

  const [grantOpen, setGrantOpen] = useState(false);
  const [grant, setGrant] = useState({
    userId: '', kind: 'percent' as RewardKind, value: 10,
    maxDiscount: 200 as number | null, expiryDays: 30, note: '',
  });

  const err = (e: any, fallback: string) =>
    setError(e?.response?.data?.message || e?.message || fallback);

  const patchProgram = (patch: Partial<Program>) => {
    setProgram((p) => ({ ...p, ...patch }));
    setDirty(true);
  };

  const load = useCallback(async () => {
    if (blocked || needsStudio) return;
    setLoading(true);
    setError(null);
    try {
      const [p, r, l] = await Promise.all([
        apiService.getReferralProgram(studioParam, pinStudioId),
        apiService.getReferralReport({ ...(studioParam ? { studioId: studioParam } : {}) }, pinStudioId),
        apiService.getReferralLedger({
          ...(studioParam ? { studioId: studioParam } : {}),
          ...(ledgerStatus ? { status: ledgerStatus } : {}),
          limit: 25,
        }, pinStudioId),
      ]);
      if (p?.program) {
        setProgram({ ...EMPTY_PROGRAM, ...p.program });
        setProgramExists(true);
      } else {
        setProgram(EMPTY_PROGRAM);
        setProgramExists(false);
      }
      setDirty(false);
      setReport(r ?? null);
      setLedger(l ?? null);
    } catch (e: any) {
      err(e, 'Could not load referrals');
    } finally {
      setLoading(false);
    }
  }, [blocked, needsStudio, studioParam, pinStudioId, ledgerStatus]);

  useEffect(() => { load(); }, [load]);

  const percentNeedsCap = (r?: RewardSpec | null) =>
    !!r && r.kind === 'percent' && !r.maxDiscount;

  const formInvalid =
    percentNeedsCap(program.refereeReward) || percentNeedsCap(program.referrerReward);

  const saveProgram = async (overrides?: Partial<Program>) => {
    const next = { ...program, ...(overrides ?? {}) };
    if (
      percentNeedsCap(next.refereeReward) ||
      percentNeedsCap(next.referrerReward)
    ) {
      setError('A percentage reward needs a maximum discount before it can be saved.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiService.upsertReferralProgram(
        {
          name: next.name,
          scope: next.scope,
          codePrefix: next.codePrefix || undefined,
          refereeReward: next.refereeReward,
          referrerReward: next.referrerReward ?? null,
          qualifyingEvent: next.qualifyingEvent,
          referrerCapPerMonth: next.referrerCapPerMonth,
          referrerCapLifetime: next.referrerCapLifetime,
          rewardExpiryDays: next.rewardExpiryDays,
          codeExpiryDays: next.codeExpiryDays,
          refereeRewardOnTrial: next.refereeRewardOnTrial,
          terms: next.terms,
        },
        studioParam,
        pinStudioId,
      );
      setNotice(
        programExists
          ? 'Saved.'
          : 'Created — it stays paused until you start it.',
      );
      await load();
    } catch (e: any) {
      err(e, 'Could not save the programme');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (next: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await apiService.setReferralProgramActive(next, studioParam, pinStudioId);
      setProgram((p) => ({ ...p, isActive: next }));
      setNotice(next ? 'Live — members can see it now.' : 'Paused.');
    } catch (e: any) {
      err(e, 'Could not change the programme state');
    } finally {
      setSaving(false);
    }
  };

  const createFromPreset = async () => {
    const chosen = PRESETS.find((p) => p.id === preset);
    if (!chosen) {
      // Custom: open the editor on the default draft. Nothing is saved until
      // they press Save, and the banner will then tell them it is paused.
      setCustomSetup(true);
      setDirty(true);
      setTab(0);
      return;
    }
    await saveProgram({
      refereeReward: chosen.referee,
      referrerReward: chosen.referrer,
    });
  };

  const submitGrant = async () => {
    if (grant.kind === 'percent' && !grant.maxDiscount) {
      setError('A percentage reward needs a maximum discount.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiService.grantReferralBenefit({
        userId: grant.userId.trim(),
        kind: grant.kind,
        value: Number(grant.value),
        maxDiscount: grant.kind === 'percent' ? Number(grant.maxDiscount) : undefined,
        expiryDays: Number(grant.expiryDays),
        note: grant.note || undefined,
      }, pinStudioId);
      setGrantOpen(false);
      setNotice('Reward granted.');
      await load();
    } catch (e: any) {
      err(e, 'Could not grant the reward');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id: string) => {
    const reason = window.prompt('Why is this reward being revoked?');
    if (!reason) return;
    try {
      await apiService.revokeReferralBenefit(id, reason, studioParam, pinStudioId);
      setNotice('Reward revoked.');
      await load();
    } catch (e: any) {
      err(e, 'Could not revoke the reward');
    }
  };

  const roi = useMemo(() => {
    if (!report || !report.cost) return null;
    return (report.revenueFromReferred / report.cost).toFixed(1);
  }, [report]);

  const perInvite = worstCase(program.refereeReward) + worstCase(program.referrerReward);

  // ── gates ────────────────────────────────────────────────────────────────
  if (blocked) {
    return (
      <Box p={3}>
        <Alert severity="info">
          Referrals are not enabled for this studio yet. Ask SweatSocial to turn on
          <strong> Enable Referrals</strong> and this page will start working.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box
        display="flex" justifyContent="space-between" alignItems="flex-start"
        flexWrap="wrap" gap={2} mb={2.5}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: colors.textPrimary }}>
            Referrals
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            Members invite friends. Both get a reward once the friend actually turns up.
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5}>
          {isSuperAdmin && (
            <StudioSelector
              value={selectedStudio} onChange={setSelectedStudio} required minWidth={240}
            />
          )}
          {pickBranch && (
            <Box minWidth={240}>
              <BranchSelect value={branch} onChange={setBranch} minRole="owner" />
            </Box>
          )}
          <Tooltip title="Reload">
            <span>
              <Button variant="outlined" onClick={load} disabled={loading || needsStudio}>
                <Refresh fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {needsStudio ? (
        <Alert severity="info">
          {pickBranch ? 'Pick a location' : 'Pick a studio'} to see its referral programme.
        </Alert>
      ) : loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : !programExists && !customSetup ? (
        /* ── FIRST RUN — one decision, each option priced ──────────────── */
        <SectionCard>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={3}>
            <Box
              sx={{
                width: 52, height: 52, borderRadius: '50%', bgcolor: '#fdeff5',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <CardGiftcard sx={{ fontSize: 26, color: colors.primaryDark }} />
            </Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, mt: 0.5 }}>
              Let members bring their friends
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem', color: colors.textSecondary, textAlign: 'center',
                maxWidth: 460, lineHeight: 1.6,
              }}
            >
              Pick what each side gets. You can change it any time, and nothing reaches
              members until you start it.
            </Typography>

            <Box sx={{ width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 1.25, mt: 2.5 }}>
              {PRESETS.map((p) => {
                const on = preset === p.id;
                return (
                  <Box
                    key={p.id} onClick={() => setPreset(p.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.75, p: '16px 18px',
                      borderRadius: '12px', cursor: 'pointer',
                      border: on ? `1.5px solid ${colors.primary}` : `1px solid ${colors.divider}`,
                      bgcolor: on ? '#fdeff5' : '#fff',
                    }}
                  >
                    <Box
                      sx={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0, bgcolor: '#fff',
                        border: on ? `6px solid ${colors.primary}` : `1.5px solid ${colors.greyText3}`,
                      }}
                    />
                    <Box flexGrow={1}>
                      <Typography sx={{ fontSize: '0.9375rem', fontWeight: on ? 700 : 600 }}>
                        {p.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: colors.textSecondary, mt: 0.25 }}>
                        {p.sub}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{ fontSize: '0.8125rem', fontWeight: 700, whiteSpace: 'nowrap', color: on ? colors.textPrimary : colors.textSecondary }}
                    >
                      {p.cost}
                    </Typography>
                  </Box>
                );
              })}

              <Box
                onClick={() => setPreset('custom')}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.75, p: '16px 18px',
                  borderRadius: '12px', cursor: 'pointer', bgcolor: '#fff',
                  border: preset === 'custom'
                    ? `1.5px solid ${colors.primary}`
                    : `1px dashed ${colors.divider}`,
                }}
              >
                <Box
                  sx={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0, bgcolor: '#fff',
                    border: preset === 'custom'
                      ? `6px solid ${colors.primary}`
                      : `1.5px solid ${colors.greyText3}`,
                  }}
                />
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: colors.textSecondary }}>
                  Set it up myself
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained" size="large" onClick={createFromPreset} disabled={saving}
              sx={{ mt: 2.5, px: 3.5 }}
            >
              {saving ? 'Creating…' : 'Create the offer'}
            </Button>
            <Typography sx={{ fontSize: '0.75rem', color: colors.greyText2, mt: 0.5 }}>
              Saved as paused — you start it when you're ready
            </Typography>
          </Box>
        </SectionCard>
      ) : (
        <>
          {programExists ? (
            <StatusBanner
              isActive={!!program.isActive}
              canActivate={canActivate}
              busy={saving}
              onToggle={toggleActive}
            />
          ) : (
            <Alert severity="info" sx={{ mb: 2.5, borderRadius: '14px' }}>
              Nothing is saved yet. Set the two rewards below and save — it stays
              <strong> paused</strong> until you start it, so nothing reaches members
              before you are ready.
            </Alert>
          )}

          {programExists && (
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2.5 }}>
              <Tab icon={<CardGiftcard fontSize="small" />} iconPosition="start" label="The offer" />
              <Tab icon={<Insights fontSize="small" />} iconPosition="start" label="Results" />
              <Tab icon={<Redeem fontSize="small" />} iconPosition="start" label="Rewards given" />
            </Tabs>
          )}

          {/* ── THE OFFER ─────────────────────────────────────────────── */}
          {tab === 0 && (
            <Box display="flex" flexDirection="column" gap={1.75}>
              <Grid container spacing={1.75}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <RewardSentence
                    title="The friend gets"
                    subject="The friend"
                    value={program.refereeReward}
                    onChange={(r) => patchProgram({ refereeReward: r ?? program.refereeReward })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <RewardSentence
                    title="Your member gets"
                    subject="Your member"
                    value={program.referrerReward}
                    onChange={(r) => patchProgram({ referrerReward: r })}
                    allowNone
                    footer={
                      program.referrerReward ? (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Eyebrow>Paid out when</Eyebrow>
                          <Box display="flex" gap={1} mt={1}>
                            {([
                              ['referee_attended', 'The friend attends', 'Safer — they showed up'],
                              ['referee_signup', 'The friend signs up', 'Faster — easier to game'],
                            ] as const).map(([key, label, hint]) => {
                              const on = program.qualifyingEvent === key;
                              return (
                                <Box
                                  key={key}
                                  onClick={() => patchProgram({ qualifyingEvent: key as any })}
                                  sx={{
                                    flexGrow: 1, p: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                                    border: on ? `1.5px solid ${colors.primary}` : `1px solid ${colors.divider}`,
                                    bgcolor: on ? '#fdeff5' : '#fff',
                                  }}
                                >
                                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: on ? 700 : 600, color: on ? colors.textPrimary : colors.textSecondary }}>
                                    {label}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.75rem', color: colors.greyText2, mt: 0.25 }}>
                                    {hint}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </>
                      ) : null
                    }
                  />
                </Grid>
              </Grid>

              {/* What it costs — the number the owner is actually deciding on */}
              <SectionCard>
                <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
                  <Box>
                    <Eyebrow>Worst case, per successful invite</Eyebrow>
                    <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.1, mt: 0.5 }}>
                      {inr(perInvite)}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Typography sx={{ flexGrow: 1, fontSize: '0.8125rem', color: colors.textSecondary, lineHeight: 1.6, minWidth: 220 }}>
                    {inr(worstCase(program.refereeReward))} to the friend
                    {program.referrerReward
                      ? ` plus ${inr(worstCase(program.referrerReward))} to your member`
                      : ''}
                    , if they spend the full cap.{' '}
                    <strong style={{ color: colors.textPrimary }}>
                      Your studio pays this, not SweatSocial.
                    </strong>
                  </Typography>
                  <Box textAlign="right">
                    <Eyebrow>Capped at</Eyebrow>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, mt: 0.5 }}>
                      {program.referrerCapPerMonth
                        ? `${program.referrerCapPerMonth} rewards / member / month`
                        : 'No monthly cap'}
                    </Typography>
                  </Box>
                </Box>
              </SectionCard>

              {/* Fine print — real, but not a daily decision */}
              <Box
                sx={{
                  borderRadius: '14px', border: `1px solid ${colors.divider}`,
                  bgcolor: colors.backgroundTertiary, px: 2.25, py: 1.75,
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <InfoOutlined sx={{ fontSize: 19, color: colors.greyText2 }} />
                  <Typography sx={{ flexGrow: 1, fontSize: '0.8125rem', color: colors.textSecondary }}>
                    Codes start with <strong style={{ color: colors.textPrimary }}>{program.codePrefix || '—'}</strong>
                    {' · rewards expire after '}
                    <strong style={{ color: colors.textPrimary }}>{program.rewardExpiryDays} days</strong>
                    {program.scope === 'branches' ? ' · valid across branches' : ' · this studio only'}
                    {program.refereeRewardOnTrial ? ' · usable on trials' : ' · trials excluded'}
                  </Typography>
                  <Button
                    size="small" onClick={() => setShowDetails((v) => !v)}
                    endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                    sx={{ color: colors.primaryDark, fontWeight: 600, flexShrink: 0 }}
                  >
                    {showDetails ? 'Hide' : 'Edit details'}
                  </Button>
                </Box>

                <Collapse in={showDetails}>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        fullWidth size="small" label="Programme name" value={program.name}
                        onChange={(e) => patchProgram({ name: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        fullWidth size="small" label="Code prefix" value={program.codePrefix}
                        onChange={(e) => patchProgram({ codePrefix: e.target.value.toUpperCase() })}
                        helperText="Leading letters of every member code, e.g. CHK-7F2Q9"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Valid across</InputLabel>
                        <Select
                          label="Valid across" value={program.scope}
                          onChange={(e) => patchProgram({ scope: e.target.value as any })}
                        >
                          <MenuItem value="branches">This studio and its branches</MenuItem>
                          <MenuItem value="studio">This studio only</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        fullWidth size="small" type="number" label="Reward expires after (days)"
                        value={program.rewardExpiryDays}
                        onChange={(e) => patchProgram({ rewardExpiryDays: Number(e.target.value) })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        fullWidth size="small" type="number" label="Rewards per member per month"
                        value={program.referrerCapPerMonth ?? ''}
                        onChange={(e) =>
                          patchProgram({
                            referrerCapPerMonth: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        helperText="Blank means no monthly cap"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        fullWidth size="small" type="number" label="Rewards per member, ever"
                        value={program.referrerCapLifetime ?? ''}
                        onChange={(e) =>
                          patchProgram({
                            referrerCapLifetime: e.target.value === '' ? null : Number(e.target.value),
                          })
                        }
                        helperText="Blank means no lifetime cap"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={program.refereeRewardOnTrial}
                            onChange={(e) => patchProgram({ refereeRewardOnTrial: e.target.checked })}
                          />
                        }
                        label="Let the friend spend their reward on a trial class"
                      />
                      <Typography sx={{ fontSize: '0.75rem', color: colors.greyText2 }}>
                        Off by default — a trial is already free or discounted, so this hands one
                        newcomer two things at once.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth multiline minRows={2} size="small"
                        label="Terms shown to members" value={program.terms ?? ''}
                        onChange={(e) => patchProgram({ terms: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </Box>

              <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1.25}>
                {dirty && (
                  <Typography sx={{ fontSize: '0.8125rem', color: colors.warning, fontWeight: 600, mr: 'auto' }}>
                    Unsaved changes
                  </Typography>
                )}
                <Button
                  variant="outlined" disabled={!dirty || saving}
                  onClick={() => {
                    if (!programExists) { setCustomSetup(false); setProgram(EMPTY_PROGRAM); setDirty(false); }
                    else { load(); }
                  }}
                >
                  {programExists ? 'Discard changes' : 'Back'}
                </Button>
                <Button
                  variant="contained" onClick={() => saveProgram()}
                  disabled={saving || formInvalid || !dirty}
                >
                  {saving ? 'Saving…' : 'Save the offer'}
                </Button>
              </Box>
            </Box>
          )}

          {/* ── RESULTS ───────────────────────────────────────────────── */}
          {tab === 1 && report && (
            <Box display="flex" flexDirection="column" gap={1.75}>
              {/* Is this worth it — first, because it is the only question */}
              <SectionCard>
                <Box display="flex" alignItems="center" gap={3.5} flexWrap="wrap">
                  <Box>
                    <Eyebrow>Rewards cost you</Eyebrow>
                    <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.1, mt: 0.5 }}>
                      {inr(report.cost)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: colors.greyText2 }}>
                      Discount actually taken, not issued
                    </Typography>
                  </Box>

                  <ArrowForward sx={{ color: colors.greyText3 }} />

                  <Box>
                    <Eyebrow>Those members spent</Eyebrow>
                    <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.1, mt: 0.5 }}>
                      {inr(report.revenueFromReferred)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: colors.greyText2 }}>
                      All-time, at your studio
                    </Typography>
                  </Box>

                  {roi && (
                    <Box
                      sx={{
                        ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5,
                        px: 2.5, py: 1.5, borderRadius: '12px',
                        bgcolor: '#f1f8f2', border: '1px solid #b9dcbd',
                      }}
                    >
                      <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: '#2e7d32', lineHeight: 1 }}>
                        {roi}×
                      </Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: '#4a6b4d', lineHeight: 1.4, maxWidth: 130 }}>
                        back for every rupee of reward
                      </Typography>
                    </Box>
                  )}
                </Box>
              </SectionCard>

              {/* The funnel, with drop-off between the steps */}
              <SectionCard>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, mb: 2 }}>
                  Where invites are going
                </Typography>

                <Box display="flex" alignItems="stretch">
                  {([
                    ['Codes shared', report.funnel.invited, 'Members who sent their code to someone', colors.primary, 100],
                    ['Friends joined', report.funnel.joined, 'Entered the code and got their reward', colors.primaryLight, report.funnel.joinRate],
                    ['Turned up', report.funnel.attended, 'Attended a class — your member is now paid', colors.success, report.funnel.invited ? Math.round((report.funnel.attended / report.funnel.invited) * 100) : 0],
                  ] as const).map(([label, value, hint, bar, width], i) => (
                    <React.Fragment key={label}>
                      {i > 0 && (
                        <Box
                          sx={{
                            width: 92, flexShrink: 0, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', gap: 0.5, pt: 1,
                          }}
                        >
                          <ArrowForward sx={{ fontSize: 18, color: colors.greyText3 }} />
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.textSecondary }}>
                            {i === 1 ? report.funnel.joinRate : report.funnel.attendRate}%
                          </Typography>
                        </Box>
                      )}
                      <Box flexGrow={1} display="flex" flexDirection="column" gap={1}>
                        <Box sx={{ height: 8, borderRadius: 1, bgcolor: bar, width: `${Math.max(6, width)}%` }} />
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.1 }}>
                          {value}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{label}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: colors.greyText2, lineHeight: 1.4 }}>
                          {hint}
                        </Typography>
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>

                {report.funnel.joined - report.funnel.attended > 0 && (
                  <Box
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.25, mt: 2,
                      p: '12px 14px', borderRadius: '10px', bgcolor: colors.backgroundTertiary,
                    }}
                  >
                    <InfoOutlined sx={{ fontSize: 17, color: colors.textSecondary, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.8125rem', color: colors.textSecondary, lineHeight: 1.5 }}>
                      <strong style={{ color: colors.textPrimary }}>
                        {report.funnel.joined - report.funnel.attended} friends joined but haven't been in yet.
                      </strong>
                      {' '}Their reward expires {program.rewardExpiryDays} days after they claimed it —
                      a nudge now is worth booking.
                    </Typography>
                  </Box>
                )}
              </SectionCard>

              {/* Who, specifically */}
              <Card>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: colors.backgroundTertiary }}>
                          <TableCell>Member</TableCell>
                          <TableCell>Friend</TableCell>
                          <TableCell>Stage</TableCell>
                          <TableCell align="right">Cost so far</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.rows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 5, color: colors.greyText2 }}>
                              No invites yet. Once the programme is live, members can share their code.
                            </TableCell>
                          </TableRow>
                        )}
                        {report.rows.map((r: any) => {
                          const spent =
                            (r.refereeBenefit?.redeemedAmount ?? 0) +
                            (r.referrerBenefit?.redeemedAmount ?? 0);
                          return (
                            <TableRow key={r._id} hover>
                              <TableCell>{r.referrer?.name || '—'}</TableCell>
                              <TableCell>{r.referee?.name || r.refereePhone || '—'}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={STAGE_LABEL[r.status] ?? r.status}
                                  color={STATUS_COLOR[r.status] ?? 'default'}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {spent > 0 ? inr(spent) : '₹0'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* ── REWARDS GIVEN ─────────────────────────────────────────── */}
          {tab === 2 && ledger && (
            <Box display="flex" flexDirection="column" gap={1.75}>
              <SectionCard>
                <Box display="flex" alignItems="center" gap={4} flexWrap="wrap">
                  <Box>
                    <Eyebrow>Issued</Eyebrow>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, mt: 0.5 }}>
                      {ledger.summary.issued}
                    </Typography>
                  </Box>
                  <Box>
                    <Eyebrow>Actually used</Eyebrow>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, mt: 0.5 }}>
                      {ledger.summary.redeemed}
                    </Typography>
                  </Box>
                  <Box>
                    <Eyebrow>Cost to the studio</Eyebrow>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, mt: 0.5 }}>
                      {inr(ledger.summary.cost)}
                    </Typography>
                  </Box>
                  {ledger.summary.expiringSoon > 0 && (
                    <Box
                      sx={{
                        ml: 'auto', px: 2, py: 1.25, borderRadius: '10px',
                        bgcolor: '#fff8e9', border: '1px solid #ffd88a',
                      }}
                    >
                      <Typography sx={{ fontSize: '0.8125rem', color: '#a86b00', fontWeight: 600 }}>
                        {ledger.summary.expiringSoon} unspent {ledger.summary.expiringSoon === 1 ? 'reward expires' : 'rewards expire'} this week
                      </Typography>
                    </Box>
                  )}
                </Box>
              </SectionCard>

              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status" value={ledgerStatus}
                    onChange={(e) => setLedgerStatus(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Unspent</MenuItem>
                    <MenuItem value="redeemed">Used</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                    <MenuItem value="revoked">Revoked</MenuItem>
                  </Select>
                </FormControl>
                <Box flex={1} />
                <Button variant="contained" startIcon={<Add />} onClick={() => setGrantOpen(true)}>
                  Grant a reward
                </Button>
              </Box>

              <Card>
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: colors.backgroundTertiary }}>
                          <TableCell>Member</TableCell>
                          <TableCell>Reward</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Saved them</TableCell>
                          <TableCell>Expires</TableCell>
                          <TableCell align="right"> </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ledger.rows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 5, color: colors.greyText2 }}>
                              No rewards issued yet.
                            </TableCell>
                          </TableRow>
                        )}
                        {ledger.rows.map((b: any) => (
                          <TableRow key={b._id} hover>
                            <TableCell>
                              {b.user?.name || '—'}
                              <Typography variant="caption" sx={{ display: 'block', color: colors.greyText2 }}>
                                {b.user?.phoneNumber || ''}
                              </Typography>
                            </TableCell>
                            <TableCell>{describeReward(b)}</TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {b.source === 'referral' ? 'Invite' : b.source === 'manual' ? 'Given by studio' : b.source || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" label={b.status} color={STATUS_COLOR[b.status] ?? 'default'} />
                            </TableCell>
                            <TableCell>{b.redeemedAmount != null ? inr(b.redeemedAmount) : '—'}</TableCell>
                            <TableCell>
                              {b.expiresAt ? new Date(b.expiresAt).toLocaleDateString('en-IN') : '—'}
                            </TableCell>
                            <TableCell align="right">
                              {b.status === 'active' && (
                                <Tooltip title="Revoke">
                                  <Button size="small" color="error" onClick={() => revoke(b._id)}>
                                    <Block fontSize="small" />
                                  </Button>
                                </Tooltip>
                              )}
                              {b.status === 'redeemed' && (
                                <CheckCircle sx={{ fontSize: 18, color: colors.success }} />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          )}
        </>
      )}

      {/* ── manual grant ───────────────────────────────────────────────── */}
      <Dialog open={grantOpen} onClose={() => setGrantOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant a reward</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>
            For service recovery — a cancelled class, a complaint. It behaves exactly like a
            referral reward and appears in the member's app.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth size="small" label="Member ID" value={grant.userId}
                onChange={(e) => setGrant((g) => ({ ...g, userId: e.target.value }))}
                helperText="Copy it from the member's page."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type" value={grant.kind}
                  onChange={(e) => setGrant((g) => ({ ...g, kind: e.target.value as RewardKind }))}
                >
                  {REWARD_KINDS.map((k) => (
                    <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth size="small" type="number" label="Value" value={grant.value}
                onChange={(e) => setGrant((g) => ({ ...g, value: Number(e.target.value) }))}
              />
            </Grid>
            {grant.kind === 'percent' && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth size="small" type="number" required label="Maximum discount"
                  value={grant.maxDiscount ?? ''} error={!grant.maxDiscount}
                  onChange={(e) =>
                    setGrant((g) => ({
                      ...g, maxDiscount: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth size="small" type="number" label="Expires after (days)"
                value={grant.expiryDays}
                onChange={(e) => setGrant((g) => ({ ...g, expiryDays: Number(e.target.value) }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth size="small" label="Internal note" value={grant.note}
                onChange={(e) => setGrant((g) => ({ ...g, note: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitGrant} disabled={saving || !grant.userId.trim()}>
            Grant
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReferralsPage;
